// pages/user/RequestConfirmation.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, Button, Tag, Spin, Divider } from "antd";
import {
  FiCheckCircle,
  FiTruck,
  FiClock,
  FiMail,
  FiHome,
  FiCalendar,
  FiMapPin,
  FiPackage,
  FiPhone,
  FiAlertCircle,
} from "react-icons/fi";
import { useSEO } from "../../hooks/useSEO";
import { useTheme } from "../../theme/ThemeProvider";
import UserAuth from "../../components/middleware/UserAuth";
import { PiCurrencyInrBold } from "react-icons/pi";
import {
  HOME_ROUTE,
  PROFILE_ROUTE,
  REQUEST_CONFIRMATION_ROUTE,
} from "../../routes/constant";

const InfoItem = ({ icon, label, value, badge, isDarkMode }) => (
  <div className={`flex items-start gap-3 py-3 ${isDarkMode ? "border-gray-700" : "border-gray-100"} border-b last:border-0`}>
    <div
      className={`flex items-center justify-center w-8 h-8 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
    >
      {icon}
    </div>
    <div className="flex-1">
      <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
        {label}
      </div>
      <div className={`text-sm font-medium flex items-center gap-2 flex-wrap ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
        {value || "Not provided"}
        {badge}
      </div>
    </div>
  </div>
);

const RequestConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const [serviceData, setServiceData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ============= SEO =============
  const seoParams = useMemo(() => {
    if (loading || !serviceData) return null;

    const serviceNo = serviceData.service_no || "";
    const productTitle = serviceData.variation?.title || "";
    const amount = serviceData.latestEvaluation?.evaluated_amount || 0;

    return {
      serviceNo: serviceNo,
      productTitle: productTitle,
      amount: amount,
      status: serviceData.status_label || "Pending",
    };
  }, [loading, serviceData]);

  useSEO(REQUEST_CONFIRMATION_ROUTE, seoParams);

  useEffect(() => {
    // Read data directly from location state
    if (location.state) {
      setServiceData(location.state);
    }
    setLoading(false);
  }, [location.state]);

  const handleTrackOrder = () => {
    navigate(`${PROFILE_ROUTE}?tab=orders`);
  };

  const handleGoHome = () => {
    navigate(HOME_ROUTE);
  };

  if (loading) {
    return (
      <UserAuth>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spin size="large" />
        </div>
      </UserAuth>
    );
  }

  if (!serviceData) {
    return (
      <UserAuth>
        <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <Card className="overflow-hidden border-0 shadow-sm rounded-xl">
            <div className="py-12 text-center">
              <FiAlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                No Service Request Found
              </h3>
              <p className={`text-sm mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                We couldn't find any service request details.
              </p>
              <Button
                type="primary"
                onClick={handleGoHome}
                className="mt-4 bg-green-600 hover:bg-green-700"
              >
                Go to Home
              </Button>
            </div>
          </Card>
        </div>
      </UserAuth>
    );
  }

  // Extract data from serviceData
  const serviceNo = serviceData.service_no || "";
  const status = serviceData.status || "pending";
  const statusLabel = serviceData.status_label || "Pending";
  const statusColor = serviceData.status_color || "bg-blue-100 text-blue-700";
  const isExpress = serviceData.is_express || false;
  const createdAt = serviceData.created_at || "";
  
  const addressDetails = serviceData.address_details || {};
  const address = addressDetails.address || "Not provided";
  const cityName = addressDetails.city_name || "";
  const pincode = addressDetails.pincode || "";
  const addressType = addressDetails.address_type || "Home";
  
  const product = serviceData.variation || {};
  const productTitle = product.title || "";
  
  const scheduling = serviceData.scheduling || {};
  const slot = scheduling.slot || "";
  const spd = scheduling.spd || "";
  
  const latestEvaluation = serviceData.latestEvaluation || {};
  const evaluatedAmount = latestEvaluation.evaluated_amount || 0;
  
  const statusReports = serviceData.status_reports || [];
  const firstReport = statusReports[0] || {};
  const progressPercentage = firstReport.progress_percentage || 10;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <UserAuth>
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <Card className="overflow-hidden border-0 shadow-sm rounded-xl">
          {/* Success Header */}
          <div className="pb-4 mb-4 text-center border-b border-gray-100 dark:border-gray-700">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-3 bg-green-100 rounded-full dark:bg-green-900/30">
              <FiCheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}>
              Service Request Confirmed! 🎉
            </h2>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              Thank you for choosing Tap2Cash
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
              <Tag color="green" className="text-xs rounded-full">
                Request #{serviceNo}
              </Tag>
              <Tag className={`text-xs rounded-full ${statusColor}`}>
                {statusLabel}
              </Tag>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2 mb-4">
            <Button
              type="primary"
              onClick={handleGoHome}
              icon={<FiHome size={14} />}
              className="flex-1 h-10 text-sm font-semibold bg-green-600 hover:bg-green-700"
            >
              Go to Home
            </Button>
            <Button
              onClick={handleTrackOrder}
              icon={<FiPackage size={14} />}
              className="flex-1 h-10 text-sm font-semibold"
            >
              Track Your Order
            </Button>
          </div>

          {/* Two Column Grid */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Left Column - Service Information */}
            <div>
              <h3 className={`text-sm font-semibold pb-2 mb-2 border-b ${isDarkMode ? "text-gray-200 border-gray-700" : "text-gray-800 border-gray-100"}`}>
                Service Information
              </h3>

              <InfoItem
                icon={<FiPackage className="text-green-500" size={14} />}
                label="Product"
                value={productTitle}
                isDarkMode={isDarkMode}
              />

              <InfoItem
                icon={<PiCurrencyInrBold className="text-purple-500" size={14} />}
                label="Estimated Amount"
                value={`₹${parseFloat(evaluatedAmount).toLocaleString()}`}
                isDarkMode={isDarkMode}
              />

              <InfoItem
                icon={<FiTruck className="text-orange-500" size={14} />}
                label="Pickup Type"
                value={isExpress ? "Express Pickup" : "Standard Pickup"}
                badge={
                  isExpress && (
                    <Tag color="orange" className="text-[10px] rounded-full">
                      Priority
                    </Tag>
                  )
                }
                isDarkMode={isDarkMode}
              />

              <InfoItem
                icon={<FiClock className="text-cyan-500" size={14} />}
                label="Status"
                value={statusLabel}
                badge={
                  <Tag className={`text-[10px] rounded-full ${statusColor}`}>
                    {statusLabel}
                  </Tag>
                }
                isDarkMode={isDarkMode}
              />

              <InfoItem
                icon={<FiClock className="text-gray-500" size={14} />}
                label="Created At"
                value={formatDate(createdAt)}
                isDarkMode={isDarkMode}
              />
            </div>

            {/* Right Column - Address Information */}
            <div>
              <h3 className={`text-sm font-semibold pb-2 mb-2 border-b ${isDarkMode ? "text-gray-200 border-gray-700" : "text-gray-800 border-gray-100"}`}>
                Pickup Address
              </h3>

              <InfoItem
                icon={<FiMapPin className="text-red-500" size={14} />}
                label="Address"
                value={address}
                isDarkMode={isDarkMode}
              />

              {cityName && (
                <InfoItem
                  icon={<FiMapPin className="text-green-500" size={14} />}
                  label="City"
                  value={cityName}
                  isDarkMode={isDarkMode}
                />
              )}

              {pincode && (
                <InfoItem
                  icon={<FiMapPin className="text-purple-500" size={14} />}
                  label="Pincode"
                  value={pincode}
                  isDarkMode={isDarkMode}
                />
              )}

              <InfoItem
                icon={<FiHome className="text-blue-500" size={14} />}
                label="Address Type"
                value={addressType}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>

          {/* Schedule Information */}
          {!isExpress && (slot || spd) && (
            <div className="mt-4">
              <h3 className={`text-sm font-semibold pb-2 mb-2 border-b ${isDarkMode ? "text-gray-200 border-gray-700" : "text-gray-800 border-gray-100"}`}>
                Pickup Schedule
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {spd && (
                  <InfoItem
                    icon={<FiCalendar className="text-purple-500" size={14} />}
                    label="Preferred Date"
                    value={spd}
                    isDarkMode={isDarkMode}
                  />
                )}
                {slot && (
                  <InfoItem
                    icon={<FiClock className="text-blue-500" size={14} />}
                    label="Time Slot"
                    value={slot}
                    isDarkMode={isDarkMode}
                  />
                )}
              </div>
            </div>
          )}

          {/* Express Pickup Info */}
          {isExpress && (
            <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? "bg-orange-900/20 border border-orange-800" : "bg-orange-50 border border-orange-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <FiTruck className="text-orange-500" size={16} />
                <span className={`text-sm font-semibold ${isDarkMode ? "text-orange-400" : "text-orange-600"}`}>
                  Express Pickup Confirmed
                </span>
              </div>
              <p className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                A pickup partner will be assigned within 30 minutes. You will receive SMS/WhatsApp updates shortly.
              </p>
            </div>
          )}

          {/* Progress Indicator */}
          {progressPercentage > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  Request Progress
                </span>
                <span className={`text-xs font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {progressPercentage}%
                </span>
              </div>
              <div className="w-full h-2 overflow-hidden bg-gray-200 rounded-full dark:bg-gray-700">
                <div
                  className="h-full transition-all duration-500 bg-green-500 rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Next Steps */}
          <Divider className={`my-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`} />

          <div className="mb-4">
            <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              What's Next?
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-green-500 rounded-full mt-0.5 flex-shrink-0">
                  1
                </div>
                <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  Our partner will contact you to confirm the pickup
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-green-500 rounded-full mt-0.5 flex-shrink-0">
                  2
                </div>
                <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  Device will be picked up from your address
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-green-500 rounded-full mt-0.5 flex-shrink-0">
                  3
                </div>
                <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  Payment will be transferred after verification
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className={`mt-4 pt-4 text-center border-t ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}>
            <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              <FiMail className="inline mr-1" size={12} />
              A confirmation email has been sent to your registered email address
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              <FiPhone className="inline mr-1" size={12} />
              For any assistance, call us at 7404252555
            </p>
          </div>
        </Card>
      </div>
    </UserAuth>
  );
};

export default RequestConfirmation;